import { useId, useRef, useState } from 'react'

import { controlIcons } from '../../constants/assets'
import { useClickOutside } from '../../hooks/useClickOutside'
import { cn } from '../../utils/cn'

export type SelectOption<TValue extends string> = {
  value: TValue
  label: string
}

type SelectProps<TValue extends string> = {
  /** Names the control for assistive tech; the visible caption lives in `FilterField`. */
  label: string
  value: TValue
  options: SelectOption<TValue>[]
  onChange: (value: TValue) => void
  /** Distance between the trigger and the panel, in px. Matches the design per field. */
  panelGap?: number
  /** Trigger height in px. The bookings filters use 65, the drivers filters 59. */
  height?: number
}

export const Select = <TValue extends string>({
  label,
  value,
  options,
  onChange,
  panelGap = 10,
  height = 65,
}: SelectProps<TValue>) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const listId = useId()

  useClickOutside(containerRef, () => setOpen(false), open)

  const selected = options.find((option) => option.value === value)

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => setOpen((isOpen) => !isOpen)}
        style={{ height }}
        className="flex w-full items-center justify-between gap-[12px] rounded-[12px] border border-solid border-dash-field bg-white px-[18px]"
      >
        <span className="truncate text-left font-host text-[16px] font-medium leading-normal text-dash-field-text">
          {selected?.label ?? label}
        </span>
        <img
          alt=""
          aria-hidden
          src={controlIcons.selectCaret}
          className={cn(
            'block size-[30px] max-w-none shrink-0 object-cover transition-transform duration-200 motion-reduce:transition-none',
            open && '-scale-y-100',
          )}
        />
      </button>

      {open && (
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          style={{ top: `calc(100% + ${panelGap}px)` }}
          className="absolute inset-x-0 z-20 flex flex-col gap-[12px] rounded-[12px] border border-solid border-dash-field bg-white px-[13px] py-[16px] shadow-dash-card"
        >
          {options.map((option) => {
            const isSelected = option.value === value

            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full truncate text-left font-host text-[16px] font-medium leading-normal',
                    isSelected ? 'text-dash-link' : 'text-black',
                  )}
                >
                  {option.label}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
